import type { ICache } from '@auth0/auth0-react';
import LogRocket from 'logrocket';

/**
 * Custom cache implementation for Auth0 that uses IndexedDB to avoid localStorage size limits
 * and truncation issues with large refresh tokens.
 *
 * IndexedDB provides much larger storage capacity (typically 50MB+) compared to localStorage (5-10MB).
 */
export class Auth0IndexedDBCache implements ICache {
  private dbName = 'auth0-cache';
  private storeName = 'keyval';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  async allKeys(): Promise<string[]> {
    try {
      const db = await this.getDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const request = store.getAllKeys();

      return new Promise((resolve, reject) => {
        request.onsuccess = (): void => resolve(request.result as string[]);
        request.onerror = (): void => {
          LogRocket.error('Error getting all keys from IndexedDB', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      LogRocket.error('Error in allKeys', error);
      return [];
    }
  }

  async get<T = unknown>(key: string): Promise<T | undefined> {
    try {
      const db = await this.getDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const request = store.get(key);

      return new Promise((resolve, reject) => {
        request.onsuccess = (): void => {
          const result = request.result as T | undefined;
          resolve(result);
        };
        request.onerror = (): void => {
          LogRocket.error('Error getting value from IndexedDB', { key, error: request.error });
          reject(request.error);
        };
      });
    } catch (error) {
      LogRocket.error('Error in get', { key, error });
      return undefined;
    }
  }

  async set<T = unknown>(key: string, entry: T): Promise<void> {
    try {
      const db = await this.getDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const request = store.put(entry, key);

      return new Promise((resolve, reject) => {
        request.onsuccess = (): void => resolve();
        request.onerror = (): void => {
          LogRocket.error('Error setting value in IndexedDB', { key, error: request.error });
          reject(request.error);
        };
      });
    } catch (error) {
      LogRocket.error('Error in set', { key, error });
      throw error;
    }
  }

  async remove(key: string): Promise<void> {
    try {
      const db = await this.getDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const request = store.delete(key);

      return new Promise((resolve, reject) => {
        request.onsuccess = (): void => resolve();
        request.onerror = (): void => {
          LogRocket.error('Error removing value from IndexedDB', { key, error: request.error });
          reject(request.error);
        };
      });
    } catch (error) {
      LogRocket.error('Error in remove', { key, error });
      throw error;
    }
  }

  private async getDB(): Promise<IDBDatabase> {
    if (this.db) {
      return this.db;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = (): void => {
        LogRocket.error('Error opening IndexedDB', request.error);
        reject(request.error);
      };

      request.onsuccess = (): void => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event): void => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
    });
  }
}
